import { useEffect, useState } from "react";
import { fetchRequestStatusCount } from "../api/logInsightsApi";
import {
  fetchComplianceStatus,
  fetchNonCompliantByTeam,
  type LogReviewChartFilters,
} from "../api/logReviewApi";
import type { LogInsightRole, PieChartDatum, TreeMapNode } from "../app/types/logReview";

export interface LogInsightsChartsData {
  complianceStatus: PieChartDatum[];
  requestStatus: PieChartDatum[];
  teamHeatmap: TreeMapNode[];
}

export type LogInsightsChartKey = keyof LogInsightsChartsData;

export interface UseLogInsightsChartsResult extends LogInsightsChartsData {
  /** Per-chart loading flag — each chart resolves independently. */
  loading: Record<LogInsightsChartKey, boolean>;
  /** Per-chart error message — set only for the chart whose fetch failed. */
  error: Record<LogInsightsChartKey, string | null>;
}


const EMPTY: LogInsightsChartsData = {
  complianceStatus: [],
  requestStatus: [],
  teamHeatmap: [],
};

const CHART_KEYS: LogInsightsChartKey[] = [
  "complianceStatus",
  "requestStatus",
  "teamHeatmap",
];

const allLoading = (v: boolean): Record<LogInsightsChartKey, boolean> =>
  Object.fromEntries(CHART_KEYS.map((k) => [k, v])) as Record<
    LogInsightsChartKey,
    boolean
  >;
const allError = (): Record<LogInsightsChartKey, string | null> =>
  Object.fromEntries(CHART_KEYS.map((k) => [k, null])) as Record<
    LogInsightsChartKey,
    string | null
  >;

/**
 * Fetches the three Log Reviewer / Approver Insights charts for the applied
 * filter set:
 *   - Compliance Status Overview (`getComplianceStatusCount`)
 *   - Request Status             (`getRequestStatusCount`)
 *   - Non-Compliant By Team      (`getNonComplianceTeam`)
 *
 * Each chart fires its OWN request and updates its OWN data / loading / error
 * slice, so a fast endpoint renders immediately instead of waiting on the
 * slowest one. Aborts every in-flight request on unmount / re-run.
 *
 * `enabled` gates the first fetch until the top filter cascade has applied —
 * without it the page fetches once on mount with empty default filters that
 * the cascade's initial apply then aborts and refires.
 *
 * `role` picks the Request Status vocabulary the donut always shows, since
 * the two queues do not share one (Acknowledged vs Approved).
 */
export const useLogInsightsCharts = (
  role: LogInsightRole,
  filters: LogReviewChartFilters,
  reloadKey = 0,
  enabled = true,
): UseLogInsightsChartsResult => {
  const [data, setData] = useState<LogInsightsChartsData>(EMPTY);
  const [loading, setLoading] = useState<Record<LogInsightsChartKey, boolean>>(() =>
    allLoading(true),
  );
  const [error, setError] = useState<Record<LogInsightsChartKey, string | null>>(() =>
    allError(),
  );

  // Stable primitive key so the effect re-runs only on a real filter change.
  const filtersKey = `${filters.appCode.join(",")}|${filters.category.join(",")}|${filters.startDate}|${filters.endDate}`;

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(allLoading(true));
    setError(allError());

    const run = <K extends LogInsightsChartKey>(
      key: K,
      promise: Promise<LogInsightsChartsData[K]>,
    ): void => {
      promise
        .then((value) => {
          if (signal.aborted) return;
          setData((d) => ({ ...d, [key]: value }));
          setLoading((l) => ({ ...l, [key]: false }));
        })
        .catch((err: unknown) => {
          if (signal.aborted) return;
          setError((e) => ({
            ...e,
            [key]: err instanceof Error ? err.message : "Failed to load chart",
          }));
          setLoading((l) => ({ ...l, [key]: false }));
        });
    };

    run("complianceStatus", fetchComplianceStatus(filters, signal));
    run("requestStatus", fetchRequestStatusCount(role, filters, signal));
    run("teamHeatmap", fetchNonCompliantByTeam(filters, signal));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, filtersKey, reloadKey, enabled]);

  return { ...data, loading, error };
};
